// src/oura/components/visibility/QuestionsTab.tsx
//
// The questions, ten to a page: which engine named the business on each,
// whether its own site answers it, and who was named in its place. A row
// opens the question drawer with the answers themselves.

import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronLeftIcon, ChevronRightIcon, InfoIcon } from "lucide-react";
import { Tooltip } from "@component/tooltip";
import { instrumentStatus } from "../aeo/InstrumentBadge";
import { ArrowLink, EngineLogo, FOCUS_RING, NewChip, PillButton, SearchBox, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { MERCHANT_V2 } from "../../merchantV2";
import { VISIBILITY_CHECK_LABEL } from "../../lib/visibilityChecks";
import {
    lostRival,
    namedInstead,
    VIS_ENGINES,
    type EngineFilter,
    type FilterDef,
    type QuestionFilter,
    type VisEngine,
    type VisModel,
    type VisRow,
} from "../../visibility/model";
import type { CellTrend } from "../../visibility/questionTrend";
import { EngineMark, EngineMarkKey, Nil, SearchChip, SiteStatus } from "./cells";

const QUESTIONS_PER_PAGE = 10;

/** The table's fixed columns, in pixels; the question takes the rest. */
/** `view`: the merchant's "View" link column, where staff have Fix. */
const COL = { engine: 96, googleAi: 108, site: 128, instead: 176, fix: 96, view: 88 } as const;
/** Below this the question runs to a word or two a line, so the table scrolls instead. */
const QUESTION_MIN = 240;

/** Filter chips: ink-filled when pressed, with the rows behind each. */
function FilterChips({ filters, value, onChange }: { filters: FilterDef[]; value: QuestionFilter; onChange: (f: QuestionFilter) => void }) {
    // The active filter's meaning, printed under the row. "Missing you" and
    // "Your site doesn't answer" sound like the same thing and are not -- one
    // is about the engines, the other about the website -- and neither carried
    // any explanation at all (#4025 FE-10). A line of text rather than a
    // tooltip, because a tooltip is not there on touch or on a keyboard.
    const hint = filters.find((f) => f.key === value)?.hint ?? null;
    return (
        <>
        <div role="group" aria-label="Filter the questions" className="flex flex-wrap gap-2">
            {filters.map((f) => {
                const on = f.key === value;
                return (
                    <button
                        key={f.key}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onChange(on ? "all" : f.key)}
                        className={cn(
                            TYPE.tc,
                            "inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-full px-3 font-medium transition-colors",
                            // Blue when pressed, the kit's selected colour (house style 6b).
                            on ? "bg-blue-600 text-white" : "bg-white text-gray-900 ring-1 ring-inset ring-gray-200 hover:bg-gray-50",
                            FOCUS_RING,
                        )}
                    >
                        {f.label}
                        <span className={cn("tabular-nums", on ? "text-blue-100" : "text-gray-400")}>{f.count}</span>
                    </button>
                );
            })}
        </div>
        {hint && <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>{hint}</p>}
        </>
    );
}

/**
 * Spans only: the chips sit inside the phone row's button.
 *
 * Quiet on purpose. The type is grey text, not a tinted chip, and a row
 * carries a search chip only when a real Google search sits behind it: an
 * empty chip on most rows said "nothing here" ten times a page. The drawer
 * still names the absence and why, and the notice above the table says it
 * once when the whole scan has none.
 */
function QuestionChips({ row, model, since, version, staff = false }: { row: VisRow; model: VisModel; since: string | null; version?: string; staff?: boolean }) {
    return (
        <span className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
            {row.kind && (
                <span title={model.kindCopy[row.kind]?.question} className="whitespace-nowrap text-xs text-gray-500">
                    {VISIBILITY_CHECK_LABEL[row.kind]}
                </span>
            )}
            {since && <NewChip since={since} version={version} />}
            {staff && row.reasking ? (
                // Its old answers are gone and the new ones are on their way (a few minutes).
                <span title="The wording was corrected. Its answers come back in a few minutes." className="inline-flex cursor-help items-center whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                    Re-asking…
                </span>
            ) : (
                staff && row.corrected && <EditedChip corrected={row.corrected} />
            )}
            <SearchChip searches={row.searches} />
        </span>
    );
}

/**
 * Staff only (Jovi, 20 Oct): the question's wording was corrected on this scan and asked again.
 * "Edited · V2" (the original wording is V1; ourteam-backend#6249 adds the number), the history
 * on hover. An older backend without a number reads "Edited".
 */
function EditedChip({ corrected }: { corrected: NonNullable<VisRow["corrected"]> }) {
    const day = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    const entries = corrected.history?.length
        ? [...corrected.history].reverse().map((h) => `V${h.version}, ${day(h.at)}${h.by ? ` by ${h.by}` : ""}. Was: "${h.was}"`)
        : [`Edited ${day(corrected.at)}${corrected.by ? ` by ${corrected.by}` : ""}${corrected.was ? `. Was: "${corrected.was}"` : ""}`];
    return (
        <span
            title={entries.join("\n")}
            className="inline-flex cursor-help items-center whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
        >
            {corrected.version ? `Edited · V${corrected.version}` : "Edited"}
        </span>
    );
}

/** The first rival named in the business's place, and how many more. */
function InsteadNames({ names }: { names: string[] }) {
    if (names.length === 0) return <Nil label="Nobody named instead" />;
    return (
        <span className="flex min-w-0 items-baseline gap-1" title={names.join(", ")}>
            <span className="truncate text-gray-600">{names[0]}</span>
            {names.length > 1 && <span className="shrink-0 tabular-nums text-gray-400">+{names.length - 1}</span>}
        </span>
    );
}

function EngineHead({ model, engine, active }: { model: VisModel; engine: VisEngine; active: EngineFilter }) {
    const s = model.engines[engine];
    // The ChatGPT column is not always ChatGPT: on a run that fell back to
    // the API it holds OpenAI answers, and the header says so, with the run's
    // own words one hover away. Google's AI has no verdict per question yet.
    // While the app re-ask is unfinished the label carries a status ("still being
    // measured in the app"): an info icon beside the name, its words on hover
    // (#4566), not four lines of header text.
    const status = engine === "chatgpt" ? instrumentStatus(s.instrument) : null;
    const tip = status
        ? undefined
        : engine === "chatgpt"
            ? model.chatgptInstrument ?? undefined
            : engine === "ai_overview" && !s.perQuestion
                ? "Read as a total on this scan, not per question"
                : undefined;
    return (
        <th scope="col" title={tip} className={cn("px-2 py-2.5 text-center font-medium", active === engine ? "text-blue-600" : "text-gray-500")}>
            <span className="inline-flex items-center gap-1.5">
                <EngineLogo engine={engine} size={14} />
                {s.label}
                {status && (
                    <Tooltip side="top" asChild content={status}>
                        <button type="button" aria-label={`${s.label}: ${status}`} className={cn("rounded-full text-gray-400 hover:text-gray-600", FOCUS_RING)}>
                            <InfoIcon aria-hidden size={13} />
                        </button>
                    </Tooltip>
                )}
            </span>
        </th>
    );
}

function FixButton({ taskId, onFix }: { taskId: string; onFix: (taskId: string) => void }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onFix(taskId); }}
            className={cn(TYPE.tc, "shrink-0 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
        >
            View fix
        </button>
    );
}

export function QuestionsTab({
    model,
    staff,
    keySlot,
    engine,
    filters,
    filter,
    rows: filtered,
    page,
    notStored,
    onFilter,
    onPage,
    onOpen,
    onFix,
    rankFor,
    trendFor,
    newFor,
    newVersion,
    footer,
    searchNotice,
}: {
    model: VisModel;
    staff: boolean;
    /** Where to draw the key instead of the footer (the card header's corner). */
    keySlot?: HTMLElement | null;
    engine: EngineFilter;
    filters: FilterDef[];
    filter: QuestionFilter;
    /** The rows behind the active filter. */
    rows: VisRow[];
    page: number;
    /** The scan did not store full answers; rows open on the scan's excerpts. */
    notStored: boolean;
    onFilter: (f: QuestionFilter) => void;
    onPage: (p: number) => void;
    onOpen: (row: VisRow) => void;
    onFix: (taskId: string) => void;
    /**
     * Where the engine placed the business on the question, 1 = first, from
     * the answers index. Null when it was not named, the answer had no numbered
     * list, or the index is not here yet; the mark then stays a check.
     */
    rankFor?: (row: VisRow, engine: VisEngine) => number | null;
    /** Whether the question moved on that engine since the previous scan on the same questions (#4125). */
    trendFor?: (row: VisRow, engine: VisEngine) => CellTrend | null;
    /**
     * The day of the earlier scan a question is new against, or null when it is
     * not new or that cannot be told (no earlier scan loaded).
     */
    newFor?: (row: VisRow) => string | null;
    /** The question set the new questions are new in ("v3"), for the chip. Both roles. */
    newVersion?: string;
    /** Staff: the searches the set does not cover. */
    footer?: ReactNode;
    /** Above the rows: why the search chips are empty, and what to do (staff). */
    searchNotice?: ReactNode;
}) {
    // Typing finds a question by its words, across every page (Jack, 22 Oct 2026:
    // a client asks about one question and it was five pages of clicking). A
    // search, or "Show all", lists every match on one page.
    const [query, setQuery] = useState("");
    const [showAll, setShowAll] = useState(false);
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const rows = words.length ? filtered.filter((r) => words.every((w) => r.text.toLowerCase().includes(w))) : filtered;
    const perPage = showAll || words.length ? Math.max(1, rows.length) : QUESTIONS_PER_PAGE;
    const pages = Math.max(1, Math.ceil(rows.length / perPage));
    const current = Math.min(page, pages);
    const slice = rows.slice((current - 1) * perPage, current * perPage);
    const first = rows.length ? (current - 1) * perPage + 1 : 0;
    const last = Math.min(rows.length, current * perPage);
    // Under "Lost to", that rival leads the names, so the column shows the
    // one the filter is about.
    const lead = lostRival(filter);
    const instead = (row: VisRow) => namedInstead(row, engine, lead);
    const perQuestion = VIS_ENGINES.filter((e) => model.engines[e].perQuestion);
    // The table shows every engine's column; the phone rows only the ones
    // with a verdict per question. Either way the key names a mark on screen.
    const unmeasured = slice.some((r) => VIS_ENGINES.some((e) => r.cells[e] === null));
    const rank = (row: VisRow, e: VisEngine) => rankFor?.(row, e) ?? null;
    // The key explains "#3" only on a page that shows one.
    const ranked = slice.some((r) => VIS_ENGINES.some((e) => rank(r, e) !== null));
    const trended = slice.some((r) => VIS_ENGINES.some((e) => (trendFor?.(r, e) ?? null) !== null));
    // The merchant's View column is the redesign's, so only with its flag on.
    const showView = !staff && MERCHANT_V2;
    const tableWidth = QUESTION_MIN + 2 * COL.engine + COL.googleAi + (model.hasSite ? COL.site : 0) + COL.instead + (staff ? COL.fix : showView ? COL.view : 0);

    return (
        <div>
            {keySlot && slice.length > 0 && createPortal(
                <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-gray-500">
                    <span className="font-medium text-gray-700">Key:</span>
                    <EngineMarkKey unmeasured={unmeasured} ranked={ranked} trended={trended} />
                </span>,
                keySlot,
            )}
            <div className="border-t border-gray-100 px-5 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <FilterChips filters={filters} value={filter} onChange={onFilter} />
                    <SearchBox value={query} onChange={setQuery} placeholder="Find a question" label="Find a question" className="w-full sm:w-56" />
                </div>
                {/* WHICH ENGINE this table is showing, and where that was set
                    (#4025 FE-11). The engine is chosen by a switch at the top
                    of the page, far above the table it filters, so a reader who
                    had narrowed to one engine met a shorter table with nothing
                    on it saying why. There is deliberately no second engine
                    control here: two controls for one setting is how they get
                    out of step. */}
                {engine !== "all" && (
                    <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                        Showing <span className="font-medium text-gray-700">{model.engines[engine].label}</span> only.
                        Use the engine switch at the top of the page to change it.
                    </p>
                )}
            </div>
            {searchNotice}
            {notStored && (
                <p className={cn(TYPE.tm, "m-0 border-t border-gray-100 bg-gray-50 px-5 py-2.5 text-gray-500")}>
                    Full answers were not stored on this scan. Each question opens on the excerpt the scan kept.
                </p>
            )}

            {slice.length === 0 ? (
                <div className={cn(TYPE.tb, "flex flex-wrap items-center gap-3 border-t border-gray-100 px-5 py-8 text-gray-500")}>
                    {words.length ? `No question has "${query.trim()}".` : "No question matches this filter."}
                    {words.length > 0 && <ArrowLink onClick={() => setQuery("")}>Clear the search</ArrowLink>}
                    {!words.length && filter !== "all" && <ArrowLink onClick={() => onFilter("all")}>Show all questions</ArrowLink>}
                </div>
            ) : (
                <>
                    {/* Stacked below 1280px: seven columns in less than that
                        left the question a word or two wide. */}
                    <div className="xl:hidden">
                        {slice.map((row) => {
                            const names = instead(row);
                            return (
                                <div key={row.id} data-q-row={row.id} className="flex items-start gap-3 border-t border-gray-100 px-5 py-3 hover:bg-gray-50">
                                    <button
                                        type="button"
                                        onClick={() => onOpen(row)}
                                        className={cn("block min-w-0 flex-1 rounded text-left", FOCUS_RING)}
                                    >
                                        <span className={cn(TYPE.tb, "line-clamp-2 font-medium text-gray-900")}>{row.text}</span>
                                        <QuestionChips row={row} model={model} since={newFor?.(row) ?? null} version={newVersion} staff={staff} />
                                        <span className={cn(TYPE.tc, "mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5")}>
                                            {perQuestion.map((e) => (
                                                <span key={e} className="inline-flex items-center gap-1.5">
                                                    <EngineLogo engine={e} size={14} />
                                                    <EngineMark cell={row.cells[e]} engine={model.engines[e].label} rank={rank(row, e)} pending={e === "ai_overview" && row.googlePending} trend={trendFor?.(row, e) ?? null} />
                                                </span>
                                            ))}
                                            {model.hasSite && <SiteStatus status={row.site} />}
                                            {names.length > 0 && (
                                                <span className="inline-flex min-w-0 max-w-full items-baseline gap-1.5">
                                                    <span className="shrink-0 text-gray-400">Named instead</span>
                                                    <InsteadNames names={names} />
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                    {staff && row.fixTaskId && <FixButton taskId={row.fixTaskId} onFix={onFix} />}
                                    {showView && <ViewLink onOpen={() => onOpen(row)} />}
                                </div>
                            );
                        })}
                    </div>

                    {/* `relative`: the screen-reader labels inside are
                        positioned, and without it they escaped this scroller
                        and widened the page. */}
                    <div className="relative hidden overflow-x-auto xl:block">
                        <table className={cn(TYPE.tc, "w-full table-fixed border-collapse")} style={{ minWidth: tableWidth }}>
                            <colgroup>
                                <col />
                                <col style={{ width: COL.engine }} />
                                <col style={{ width: COL.engine }} />
                                <col style={{ width: COL.googleAi }} />
                                {model.hasSite && <col style={{ width: COL.site }} />}
                                <col style={{ width: COL.instead }} />
                                {(staff || showView) && <col style={{ width: staff ? COL.fix : COL.view }} />}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th scope="col" className="py-2.5 pl-5 pr-2 text-left font-medium text-gray-500">Question</th>
                                    {VIS_ENGINES.map((e) => <EngineHead key={e} model={model} engine={e} active={engine} />)}
                                    {model.hasSite && <th scope="col" className="px-2 py-2.5 text-center font-medium text-gray-500">Your site</th>}
                                    <th scope="col" className="py-2.5 pl-2 pr-2 text-left font-medium text-gray-500">Named instead</th>
                                    {staff ? (
                                        <th scope="col" className="py-2.5 pl-2 pr-5 text-center font-medium text-gray-500">Fix</th>
                                    ) : showView ? (
                                        <th scope="col" className="py-2.5 pl-2 pr-5"><span className="sr-only">Open</span></th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody>
                                {slice.map((row) => (
                                    // The whole row answers a pointer; the question's
                                    // own button is the control for keyboards.
                                    <tr key={row.id} data-q-row={row.id} onClick={() => onOpen(row)} className="cursor-pointer hover:bg-gray-50">
                                        <td className="h-[60px] border-t border-gray-100 py-2 pl-5 pr-2 text-left">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); onOpen(row); }}
                                                title={row.text}
                                                className={cn(TYPE.tb, "block w-full rounded text-left font-medium text-gray-900", FOCUS_RING)}
                                            >
                                                <span className="line-clamp-2">{row.text}</span>
                                            </button>
                                            <QuestionChips row={row} model={model} since={newFor?.(row) ?? null} version={newVersion} staff={staff} />
                                        </td>
                                        {VIS_ENGINES.map((e) => (
                                            <td key={e} className="border-t border-gray-100 px-2 text-center">
                                                <EngineMark cell={row.cells[e]} engine={model.engines[e].label} rank={rank(row, e)} pending={e === "ai_overview" && row.googlePending} trend={trendFor?.(row, e) ?? null} />
                                            </td>
                                        ))}
                                        {model.hasSite && (
                                            <td className="border-t border-gray-100 px-2 text-center">
                                                <SiteStatus status={row.site} />
                                            </td>
                                        )}
                                        <td className="border-t border-gray-100 pl-2 pr-2 text-left">
                                            <InsteadNames names={instead(row)} />
                                        </td>
                                        {staff ? (
                                            <td className="border-t border-gray-100 pl-2 pr-5 text-center">
                                                {row.fixTaskId
                                                    ? <FixButton taskId={row.fixTaskId} onFix={onFix} />
                                                    : <Nil label="No fix in the plan for this question" />}
                                            </td>
                                        ) : showView ? (
                                            <td className="border-t border-gray-100 pl-2 pr-5 text-right">
                                                <ViewLink onOpen={() => onOpen(row)} />
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <div className={cn(TYPE.tc, "flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-gray-100 px-5 py-3 text-gray-500")}>
                <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="tabular-nums">
                        {first}–{last} of {rows.length}
                        {model.hasSamples && " · 2/3 means named in 2 of 3 asks"}
                    </span>
                    {slice.length > 0 && !keySlot && <EngineMarkKey unmeasured={unmeasured} ranked={ranked} trended={trended} />}
                </span>
                {!words.length && (pages > 1 || showAll) && (
                    <span className="inline-flex items-center gap-3">
                        <button type="button" onClick={() => setShowAll((v) => !v)} className={cn(FOCUS_RING, "rounded font-medium text-blue-700 hover:underline")}>
                            {showAll ? `Show ${QUESTIONS_PER_PAGE} a page` : `Show all ${rows.length}`}
                        </button>
                    </span>
                )}
                {pages > 1 && (
                    <span className="inline-flex items-center gap-2">
                        <PillButton size="sm" icon={ChevronLeftIcon} aria-label="Previous page" disabled={current <= 1} onClick={() => onPage(current - 1)} />
                        <span className="tabular-nums">{current} of {pages}</span>
                        <PillButton size="sm" icon={ChevronRightIcon} aria-label="Next page" disabled={current >= pages} onClick={() => onPage(current + 1)} />
                    </span>
                )}
            </div>
            {footer}
        </div>
    );
}

/**
 * The merchant's "View" on a row, where staff have Fix: the row already
 * opens on a press, and this says so without a hint over the table.
 */
function ViewLink({ onOpen }: { onOpen: () => void }) {
    return (
        // The row opens on a press too; this one must not open it twice.
        <span className="inline-flex shrink-0" onClick={(e) => e.stopPropagation()}>
            <ArrowLink onClick={onOpen}>View</ArrowLink>
        </span>
    );
}
